import React, { useEffect, useState, useContext } from 'react';
import { LocaleContext } from 'providers/locale-settings-context';
import { uniqBy } from 'lodash';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { Translation, useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { Tabs, TabsList, TabsTrigger } from '@cargomatic/ui';
import EventList from './EventList';
import { populateAndFormatSimplifiedEvents, formatAuditEvents, } from '../../../../helpers/events';
import CargomaticInput from '../../../v2/input';
function labelFormatter(cell, row) {
    if (row.meta && row.meta.type && row.meta.type === 'rpa-update') {
        return cell;
    }
    if (typeof cell !== 'object') {
        return React.createElement(Translation, null, (t) => t(sluggify(cell)));
    }
    return cell;
}
const dropDuplicateEvents = (events) => {
    if (!Array.isArray(events)) {
        throw new Error('Input should be an array');
    }
    return uniqBy(events, ({ application, event, object, user, timestamp }) => {
        const userEmail = (user === null || user === void 0 ? void 0 : user.email) || '';
        return `${application}-${event}-${object}-${userEmail}-${timestamp}`;
    });
};
/*
 * text property is now localized slugs
 */
const simplifiedColumns = [
    {
        dataField: 'label',
        text: 'EVENT',
        formatter: labelFormatter,
    },
    {
        dataField: 'date',
        text: 'DATE',
    },
    {
        dataField: 'updated',
        text: 'ENTERED_LAST_UPDATE',
    },
    {
        dataField: 'source',
        text: 'SOURCE',
    },
    {
        dataField: 'channel',
        text: 'CHANNEL',
    },
];
/*
 * text property is now localized slugs
 */
const allColumns = [
    {
        dataField: 'label',
        text: 'EVENT',
        formatter: labelFormatter,
    },
    {
        dataField: 'date',
        text: 'DATE',
    },
    {
        dataField: 'reported_date',
        text: 'REPORTED_DATE',
    },
    {
        dataField: 'source',
        text: 'SOURCE',
    },
    {
        dataField: 'channel',
        text: 'CHANNEL',
    },
];
const Events = ({ shipment, audits, stops, keepDuplicateRows }) => {
    const { t } = useTranslation();
    const { formatDateTime } = useContext(LocaleContext);
    const [filters, setFilters] = useState({
        rowData: [],
        columnData: [],
        isSimplifiedView: true,
        eventName: '',
        filteredRows: null,
        activeTab: 'simplified',
    });
    useEffect(() => {
        setFilters(Object.assign(Object.assign({}, filters), { rowData: getEvents(shipment, audits, stops, filters.isSimplifiedView), columnData: getColumns(filters.isSimplifiedView) }));
    }, [shipment, audits]);
    const onEventChange = (searchedEvent) => {
        if (!searchedEvent) {
            setFilters(Object.assign(Object.assign({}, filters), { eventName: '', filteredRows: null }));
        }
        else {
            const filteredRows = filters.rowData.filter((row) => {
                return row.event.toLowerCase().includes(searchedEvent.toLowerCase());
            });
            setFilters(Object.assign(Object.assign({}, filters), { eventName: searchedEvent, filteredRows }));
        }
    };
    const getEvents = (shipment, audits = [], stops = [], simplified = true) => {
        if (!keepDuplicateRows)
            audits = dropDuplicateEvents(audits);
        if (simplified) {
            const type = determineJobType(shipment);
            if (!type)
                return [];
            const rows = populateAndFormatSimplifiedEvents(type, shipment, stops, audits);
            return rows;
        }
        const rows = formatAuditEvents(formatDateTime, audits);
        return rows;
    };
    const drayageOrderTypes = [
        'import',
        'export',
        'reposition',
        'reload',
        'intermodal',
    ];
    const determineJobType = (shipment) => {
        const { shipment_type, order_type, order_movement_type } = shipment;
        if (!isIntermodalShipmentType(shipment_type)) {
            return 'shorthaul';
        }
        if (!order_type) {
            return null;
        }
        if (drayageOrderTypes.includes(order_type)) {
            return order_type;
        }
        if (!order_movement_type) {
            return null;
        }
        if (order_type === 'domestic' && order_movement_type === 'delivery') {
            return 'import';
        }
        if (order_type === 'domestic' && order_movement_type === 'pickup') {
            return 'export';
        }
    };
    const getColumns = (simple) => {
        return simple ? simplifiedColumns : allColumns;
    };
    const setToSimplifiedView = () => {
        if (!filters.isSimplifiedView) {
            setFilters(Object.assign(Object.assign({}, filters), { rowData: getEvents(shipment, audits, stops, true), isSimplifiedView: true, columnData: getColumns(true), eventName: '', filteredRows: null }));
        }
    };
    const setToAllView = () => {
        if (filters.isSimplifiedView) {
            setFilters(Object.assign(Object.assign({}, filters), { rowData: getEvents(shipment, audits, stops, false), isSimplifiedView: false, columnData: getColumns(false), eventName: '', filteredRows: null }));
        }
    };
    const findRows = () => {
        const { rowData, filteredRows } = filters;
        return filteredRows || rowData;
    };
    const handleTabChange = (value) => {
        if (value === 'simplified') {
            setToSimplifiedView();
        }
        else {
            setToAllView();
        }
    };
    return (React.createElement("div", null, filters.rowData && filters.columnData && (React.createElement("div", { className: "space-y-4" },
        React.createElement(Tabs, { value: filters.isSimplifiedView ? 'simplified' : 'all', onValueChange: handleTabChange },
            React.createElement(TabsList, null,
                React.createElement(TabsTrigger, { value: "simplified" }, t('SIMPLIFIED')),
                React.createElement(TabsTrigger, { value: "all" }, t('ALL_AUDITS')))),
        filters.rowData.length ? (React.createElement("div", { className: "space-y-4" },
            React.createElement("div", { className: "w-full md:w-60" },
                React.createElement(CargomaticInput, { value: filters.eventName || '', placeholder: t('SEARCH_BY_EVENT_NAME'), label: t('SEARCH'), info: filters.isSimplifiedView
                        ? t('SEARCH_SIMPLIFIED_EVENT')
                        : t('SEARCH_EVENT_AUDITS'), onChange: onEventChange, name: "event", type: "text" })),
            React.createElement(EventList, { rows: findRows(), columns: filters.columnData }))) : null))));
};
export default Events;
